import * as React from 'react';

import {
  AmountInput,
  CardCvvInput,
  CardExpiryInput,
  CardNumberInput,
  IframeForms,
  SenderNameInput
} from "pages/components";
import { BlockWrapper, Box, Button, Loader, GooglePayButton, TextRow, Typography, ApplePayButton } from "components";

import {
  AcsParamsName3DS2,
  ApplePayType,
  Confirmation3DS,
  Confirmation3DS2,
  PayButtons,
  IWindowCustom
} from "types";
import { ENDPOINTS } from 'endpoints';
import { TEXT } from 'texts';
import { Currency, UrlParam } from "enums";
import { observer } from 'mobx-react-lite';
import { paths } from 'routing';
import { useHistory } from 'react-router-dom';
import { useStores } from 'hooks';
import { useTranslation } from 'react-i18next';
import { formatAmount, useBreakpoints } from "utils";
import { useEffect } from "react";
import { useStyles } from './P2PForm.styles';

declare const window: IWindowCustom;

const P2PForm = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const styles = useStyles();
  const { isMd } = useBreakpoints()

  const { formStore, errorStore, appStore, dataStore } = useStores();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [formData, setFormData] = React.useState<Confirmation3DS | Confirmation3DS2 | null>(null);
  const [iframesData, setIframesData] = React.useState<Partial<Confirmation3DS2> | null>(null);
  const [fastPayButtons, setFastPayButtons] = React.useState<PayButtons>({applePay: false, googlePay: true});

  const formRef = React.useRef<HTMLFormElement>(null);
  const payCardRef = React.useRef<HTMLDivElement>(null);
  const mainFormRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    (async function initForm () {
      try {
        setIsLoading(true);

        formStore.checkCardNumber()
          .then((res) => {
            formStore.setPaymentRules(res);
            res.googlePaymentData && formStore.setGooglePaymentData(res.googlePaymentData);
            res.applePayMerchantId && formStore.setApplePayMerchantId(res.applePayMerchantId);
          })
          .then(formStore.updateCommision)
          .finally(() => setIsLoading(false))
      } catch (error) {
        errorStore.setError({ title: TEXT.error, description: (error as Error).message });
      }
    })()
  }, []);

  React.useEffect(() => {
    if (formData && formRef.current) {
      formRef.current.submit();
    }
  }, [formData]);

  React.useEffect(() => {
    if (formStore.cardNumber.value.length === 16 && formStore.expireDate.value.length === 0) {
      mainFormRef.current?.elements.namedItem('credit-expires') &&
        (mainFormRef.current?.elements.namedItem('credit-expires') as HTMLInputElement).click();
    }
    if (
      formStore.cardNumber.value.length === 16 &&
      formStore.expireDate.value.length === 4 &&
      formStore.cvv.value.length === 0
    ) {
      mainFormRef.current?.elements.namedItem('credit-cvc') &&
        (mainFormRef.current?.elements.namedItem('credit-cvc') as HTMLInputElement).click();
    }
    if (
      formStore.cardNumber.value.length === 16 &&
      formStore.expireDate.value.length === 4 &&
      formStore.cvv.value.length === 3
    ) {
      mainFormRef.current?.elements.namedItem('amount') &&
        (mainFormRef.current?.elements.namedItem('amount') as HTMLInputElement).click();
    }
  }, [formStore.cardNumber.value.length, formStore.expireDate.value.length, formStore.cvv.value.length]);


  useEffect(() => {
    if (
      formStore.applePayMerchantId
      && window.ApplePaySession
      && window.ApplePaySession.canMakePayments()
    ) {
      window.ApplePaySession.canMakePaymentsWithActiveCard(formStore.applePayMerchantId)
        .then((success: any) => {
          if (success) {
            initApplePay('plain')
          } else {
            // initApplePay('setup');
            if (formStore.googlePaymentData?.googlePayMerchantId && formStore.googlePaymentData?.googlePayGatewayMerchantId) {
              initGooglePay();
            }
          }
        })
    } else if (formStore.googlePaymentData?.googlePayMerchantId && formStore.googlePaymentData?.googlePayGatewayMerchantId) {
      initGooglePay();
    }
  },[formStore.applePayMerchantId, formStore.googlePaymentData]);

  const initApplePay = (type: ApplePayType) => {
    setFastPayButtons({ applePay: type, googlePay: false })
  }
  const initGooglePay = () => {
    setFastPayButtons({ applePay: false, googlePay: true });
  }

  const submit3DsHandler = (response: Confirmation3DS2): void => {
      if (response.ascUrl) {
        const url = new URL(ENDPOINTS.CONFIRM_3DS);

        url.searchParams.append(UrlParam.SPLIT_ID, appStore.id);
        url.searchParams.append(UrlParam.ORDER_ID, appStore.orderId);
        url.searchParams.append(UrlParam.LANG, appStore.lang);
        url.searchParams.append(UrlParam.REDIRECT_URL, window.location.origin);

        dataStore.selectedParticipant &&
        url.searchParams.append(UrlParam.PARTICIPANT_ID, `${dataStore.selectedParticipant.participantId}`);

        const thermUrlParam = { name: 'TermUrl' as AcsParamsName3DS2, value: url.toString() };

        const data: Confirmation3DS2 = {
          ...response,
        };

        data.acsParams.push(thermUrlParam);
        setFormData(data);
      }

      if (response.methodURL || response.methodURLServer) {
        setIframesData({
          methodURL: response.methodURL,
          methodURLServer: response.methodURLServer,
          methodDataPacked: response.methodDataPacked,
          serverTransID: response.serverTransID,
        });
        setIsLoading(true);
      }
  }

  const submit = async () => {
    try {
      setIsLoading(true);
      const response = await formStore.submit();
      setIsLoading(false);

      if (response.orderStatus) {
        const isResponseSuccess = response.orderStatus === 'CONFIRMED' || response.orderStatus === 'PROCESSED';
        history.push(isResponseSuccess ? paths.success : paths.error);
      }

      if (response.confirmationType === '2DS') {
        appStore.setOrderId(response.orderId);
        history.push(paths.lookup);
      }

      if (response.confirmationType === 'OTP' && response.confirmationOTP?.otpSent) {
        appStore.setOrderId(response.orderId);
        history.push(paths.lookup);
      }

      if (response.confirmationType === '3DS' && response.confirmation3DS) {
        const data: Confirmation3DS = {
          ...response.confirmation3DS,
          acsParams: response.confirmation3DS.acsParams.map((param) => {
            if (param.name === 'TermUrl') {

              const url = new URL(ENDPOINTS.CONFIRM_3DS);

              url.searchParams.append(UrlParam.SPLIT_ID, appStore.id);
              url.searchParams.append(UrlParam.ORDER_ID, response.orderId);
              url.searchParams.append(UrlParam.LANG, appStore.lang);
              url.searchParams.append(UrlParam.REDIRECT_URL, window.location.origin);

              dataStore.selectedParticipant &&
                url.searchParams.append(UrlParam.PARTICIPANT_ID, `${dataStore.selectedParticipant.participantId}`);

              return { name: param.name, value: url.toString() };
            }
            return param;
          }),
        };
        setFormData(data);
      }
      if (response.confirmationType === '3DS2' && response.confirmation3DS2) {
        appStore.setOrderId(response.orderId);
        submit3DsHandler(response.confirmation3DS2);
      }
    } catch (error) {
      setIsLoading(false);
      errorStore.setError({ title: TEXT.error, description: (error as Error).message });
    }
  };

  const confirm = async () => {
    try {
      setIsLoading(true);
      const response = await formStore.confirm();

      setIsLoading(false);

      if (response.orderStatus) {
        const isResponseSuccess = response.orderStatus === 'CONFIRMED' || response.orderStatus === 'PROCESSED';

        history.push(isResponseSuccess ? paths.success : paths.error);
      }

      if (response.confirmation3DS2) {
        submit3DsHandler(response.confirmation3DS2);
      }

    } catch (error) {
      setIsLoading(false);
      errorStore.setError({ title: TEXT.error, description: (error as Error).message });
    }
  }

  const handleSubmitIframes = () => {
    setIframesData(null);
    setIsLoading(false);
    confirm();
  }


  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    formStore.clearFastPayValues();
    formStore.validate(['cardNumber', 'expireDate', 'cvv', 'amount']);
    if (formStore.isP2PValid) {
      if (dataStore.transferData.needExtraData) {
        history.push(paths.transfer);
      } else {
        submit();
      }
    }
  };

  const handlePayCard = () => {
    formStore.togglePaymentCardVisible();
    setTimeout(() => {
      payCardRef.current && payCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100)}

  const hasAmount = !!dataStore.appData?.amount;
  const hasPurpose = !!dataStore.appData?.recipient.purposeText;

  const GPayButtonCondition = fastPayButtons.googlePay
    && formStore.googlePaymentData?.googlePayMerchantId
    && formStore.googlePaymentData?.googlePayGatewayMerchantId;

  const ApplePayButtonCondition = fastPayButtons.applePay && formStore.applePayMerchantId;

  return (
    <>
      {isLoading && <Loader />}
      {formData && (
        <form ref={formRef} action={formData.ascUrl} method="POST" style={{display: "none"}}>
          {formData.acsParams.map((param: { name: string; value: string }, i: number) => (
            <input hidden readOnly key={i} name={param.name} value={param.value} />
          ))}
        </form>
      )}
      <IframeForms iframesData={iframesData} onIframesSend={handleSubmitIframes}/>
      <form onSubmit={handleSubmit} ref={mainFormRef}>
        <BlockWrapper mt={2}>
          <Typography textType="hint" textColor="grey" align="center">
            {t(TEXT.amount)}
          </Typography>
          <AmountInput disabled={isLoading} />
        </BlockWrapper>

        {((hasAmount || hasPurpose) && !isMd) &&
          <BlockWrapper mt={3} className={styles.details}>
            {hasAmount && <TextRow
              title={t(TEXT.totalAmount)}
              text={`${formatAmount({ amount: dataStore.appData?.amount })} ${Currency.UAH}`}
            />}
            {hasPurpose && <TextRow
              title={t(TEXT.comment)}
              subtitle={dataStore.appData?.recipient.purposeText}
            />}
          </BlockWrapper>
        }

        <BlockWrapper mt={2}>
          <SenderNameInput disabled={isLoading} />
        </BlockWrapper>

        <Box mt={2}>
          {GPayButtonCondition &&
            <GooglePayButton onSend3DSData={submit3DsHandler} />
          }
          {ApplePayButtonCondition &&
            <ApplePayButton onSend3DSData={submit3DsHandler} type={fastPayButtons.applePay} />
          }

          <Button
            style={{ marginTop: "15px" }}
            buttonType="round"
            background="transparent"
            fullWidth
            type="button"
            disabled={isLoading || formStore.isDataLoading}
            onClick={handlePayCard}
          >
            <Typography textType="button" textColor="black">
              {dataStore.transferData.needExtraData ? t(TEXT.continue) : t(TEXT.payCard)}
            </Typography>
          </Button>
        </Box>

        <div ref={payCardRef}>
          {formStore.isPaymentCardVisible &&
            <BlockWrapper mt={2}>
              <CardNumberInput disabled={isLoading} />
              <Box mt={1} display="flex" justifyContent="space-between">
                <Box>
                  <CardExpiryInput disabled={isLoading} />
                </Box>
                <Box/>
                <Box>
                  <CardCvvInput disabled={isLoading} />
                </Box>
              </Box>

              <Box mt={3} mb={1}>
                <Button
                  buttonType="round"
                  background="gradient"
                  fullWidth
                  type="submit"
                  disabled={isLoading || !formStore.isP2PValid || formStore.isDataLoading}
                >
                  <Typography textType="button" textColor="white">
                    {t(TEXT.submit)}
                  </Typography>
                </Button>
              </Box>
            </BlockWrapper>
          }
        </div>
      </form>
    </>
  );
};

export default observer(P2PForm);
